import React, { useState } from "react";
import ErrorModal from "web/react/components/checkout/error-modal/error-modal";
import { Button, ButtonProps } from "web/react/emo/button";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import {
    PurchaseIntentCheckoutType,
    PurchaseIntentPurchaseType,
} from "web/react/pages/checkout/checkout.context";
import {
    ProductPurchaseType,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import { isSelectedSize } from "web/react/pages/product/in-stock/in-stock-product-page/utils";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import requester from "web/script/modules/requester";
import { removeCurrencyChars } from "web/script/utils/currency-symbols";
import { buildLanguageAwareUrlPath } from "web/script/utils/url";
import uuid4 from "web/script/utils/uuid";
import { ProductBuyOptionsSerializer } from "web/types/serializers";

const NATIVE_CHECKOUT_ENDPOINT = "/checkout/now/";
export const V2_CHECKOUT_ENDPOINT = "/rest_api/checkout/v2/session/";

enum BuyOptionsRequestErrorCode {
    GENERIC_ERROR = "generic_error",
    CHECKOUT_SIZE_NOT_IN_STOCK = "CheckoutSizeNotInStock",
}

type ErrorModalProperties = {
    title: string;
    desc: string;
    ctaText: string;
    open: boolean;
};

export type CheckoutBuyButtonProps = {
    buyOption: ProductBuyOptionsSerializer;
    buyOptionPosition: number;
    isComparison?: boolean;
    buttonProps?: Partial<ButtonProps>;
    sendExtraAnalytics?: boolean;
    parentArea?: "price_comparison" | "product_details";
};

export function CheckoutBuyButton({
    buyOption,
    buyOptionPosition,
    isComparison = false,
    sendExtraAnalytics = true,
    buttonProps,
    parentArea,
}: CheckoutBuyButtonProps): React.ReactElement {
    const {
        link_id: linkId,
        retailer_slug: retailerSlug,
        price,
        promotions,
        free_shipping: hasFreeShipping,
        shipping_cost: shippingCostLabel,
    } = buyOption;
    const {
        product,
        activeProduct,
        sizePicker,
        comparison: { closeCompareModal },
        productPurchaseType,
    } = useInStockProductPageContext();

    const { isDesktopViewport } = useDomViewport();
    const hasPromotion = promotions.length > 0;
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [errorModalProperties, setErrorModalProperties] = useState<ErrorModalProperties>({
        title: "",
        desc: "",
        ctaText: "",
        open: false,
    });

    function generateErrorModalText(errorCode: string): Partial<ErrorModalProperties> {
        switch (errorCode) {
            case BuyOptionsRequestErrorCode.CHECKOUT_SIZE_NOT_IN_STOCK:
                return {
                    title: gettext("error.oos_modal.title"), // This product is out of stock
                    desc: gettext("error.oos_modal.description"), // We're sorry, but this product is currently out of stock. We can send you a notification when it's back.
                    ctaText: gettext("general.close"), // Close
                };
            case BuyOptionsRequestErrorCode.GENERIC_ERROR:
            default:
                return {
                    title: gettext("error.general_modal.title"), // Something's gone wrong
                    desc: gettext("error.general_modal.description"), // We're sorry, but it looks like something's gone wrong. Please try again later.
                    ctaText: gettext("general.close"), // Close
                };
        }
    }

    function getAddToCartAnalyticsLabel(): string {
        if (productPurchaseType === ProductPurchaseType.CHECKOUT && parentArea) {
            return parentArea;
        }

        if (product.is_saved_for_later) {
            return "save_for_later_add_to_cart";
        }

        return "add_to_cart_button";
    }

    async function sendBuyActionAnalytics(
        buyOption: Partial<ProductBuyOptionsSerializer>
    ): Promise<any[]> {
        const analyticsPromises = [
            analytics.ecommerce("ec:addProduct", {
                name: environment.get("analyticsProduct.product_canonical_slug"),
                category: environment.get("analyticsProduct.ga_category"),
                brand: environment.get("analyticsProduct.designer_slug"),
                variant: environment.get("analyticsProduct.product_uid"),
                price: buyOption.price,
                quantity: 1,
                dimension20: environment.get("analyticsProduct.gender"),
                dimension29: environment.get("analyticsProduct.color"),
                dimension28: true,
                dimension30: false,
                dimension32: "Lyst",
                dimension34: activeProduct.selectedSizeOption?.raw_size,
                dimension23: buyOption.retailer_slug,
                metric8: buyOption.applied_discount,
                dimension31: activeProduct.selectedSizeOption?.is_final_sale,
                dimension27: buyOption.free_shipping,
            }),
            analytics.ecommerce("ec:setAction", "add"),
            analytics.event("cart", "add_item", getAddToCartAnalyticsLabel(), false, {
                product_id: product.product_id,
                product_name: environment.get("analyticsProduct.product_canonical_slug"),
                price: buyOption.price,
                currency: environment.get("currencyProps.currencyCode"),
            }),
        ];
        return Promise.all(analyticsPromises);
    }

    function normalizeShippingCost(freeShipping: boolean, shippingCost: string | null): number {
        return !freeShipping && shippingCost ? removeCurrencyChars(shippingCost) : 0;
    }

    function sendCtaAnalytic({ cta }): void {
        if (!sendExtraAnalytics) {
            return;
        }

        const category = "buy_area";
        const action = "buy_options";
        const subType = `checkout.${category}.${action}`;

        analytics.event(category, action, "", false, null, subType, {
            buy_area: {
                buy_cta: {
                    cta,
                    option: {
                        retailer: retailerSlug,
                        price: price,
                        shipping: normalizeShippingCost(!!hasFreeShipping, shippingCostLabel),
                        is_checkout: true,
                        has_promotion: hasPromotion,
                        link_id: linkId,
                        position: buyOptionPosition, // position at which the buy option is displayed to the user
                    },
                },
            },
        });
    }

    function sendCheckoutAnalytics(
        purchaseIntentSessionId: string,
        checkoutType: PurchaseIntentCheckoutType
    ): void {
        if (!sendExtraAnalytics) {
            return;
        }

        const category = "purchase_pdp";
        const action = "clicked";
        const subType = `${category}.${action}`;

        analytics.event(category, action, "", false, null, subType, {}, [
            {
                item_type: "buy_option",
                id: linkId,
                sizes: [
                    {
                        size: activeProduct.selectedSizeOption?.raw_size,
                        on_sale: activeProduct,
                        price: activeProduct.price.fullPrice,
                        current_price:
                            activeProduct.price.salePrice || activeProduct.price.fullPrice,
                        currency: environment.get("currencyProps.currencyCode"),
                    },
                ],
                retailer: {
                    slug: retailerSlug,
                },
                product: {
                    id: product.product_id,
                    slug: product.canonical_slug,
                    category: product.designer_category_url,
                },
            },
            {
                item_type: "purchase_intent_session",
                purchase_intent_session_id: purchaseIntentSessionId,
                purchase_type: PurchaseIntentPurchaseType.CHECKOUT,
                checkout_type: checkoutType,
            },
        ]);
    }

    async function callV2Endpoint(
        buyOption: ProductBuyOptionsSerializer,
        sessionId: string | null
    ): Promise<void> {
        const rawSize = buyOption.sizes.filter(
            (size) =>
                activeProduct.selectedSizeOption &&
                isSelectedSize(activeProduct.selectedSizeOption, size)
        );

        const endpoint = buildLanguageAwareUrlPath(V2_CHECKOUT_ENDPOINT);
        const requestData = {
            method: "POST",
            body: JSON.stringify({
                purchase_intent_session_id: sessionId,
                items: [
                    // we only support single item for now...
                    {
                        buy_option_id: String(buyOption.link_id),
                        size: rawSize[0].raw_size,
                    },
                ],
            }),
        };

        return requester(endpoint, requestData as any)
            .then((response) => {
                if (response.status > 299 || response.status < 200) {
                    throw new Error(
                        "Something went wrong when trying to make the checkout session."
                    );
                }
                window.location = buildLanguageAwareUrlPath("/checkout/") as string & Location;
            })
            .catch((err) => {
                return handleError(String(err));
            });
    }

    async function callEndpoint(
        endpoint: string,
        buyOption: ProductBuyOptionsSerializer,
        sessionId: string | null,
        onSuccess: (response: Record<string, any>) => void,
        onError: (error: Error) => void
    ): Promise<void> {
        sendBuyActionAnalytics(buyOption);
        const rawSize = buyOption.sizes.filter(
            (size) =>
                activeProduct.selectedSizeOption &&
                isSelectedSize(activeProduct.selectedSizeOption, size)
        );

        const requestData = {
            method: "POST",
            body: {
                buy_option: JSON.stringify({
                    icon: true, // TODO: remove when old checkout is removed
                    link_id: String(buyOption.link_id),
                    raw_size: rawSize[0].raw_size,
                    purchase_intent_session_id: sessionId,
                }),
                is_base64_encoded: false,
                // Using `_country` allows our SetCountry Middleware to specify the country of the request better.
                _country: environment.get("country"),
            },
        };

        return requester(endpoint, requestData as any)
            .then(requester.toJSON)
            .then((response) => {
                if (!response.success) {
                    let error = new Error() as any;
                    error.response = response;
                    throw error;
                } else {
                    return onSuccess(response);
                }
            })
            .catch((err) => {
                return onError(err);
            });
    }

    function handleError(errorCode: string): void {
        analytics.event("purchase_pdp", "errored", errorCode, false, null);
        const { title, desc, ctaText } = generateErrorModalText(errorCode);
        setErrorModalProperties({
            title: title as string,
            desc: desc as string,
            ctaText: ctaText as string,
            open: true,
        });
    }

    async function onNativeCheckoutClick(
        buyOption: ProductBuyOptionsSerializer,
        purchaseIntentSessionId: string
    ): Promise<void> {
        function onSuccess(response: any): void {
            window.location = response.data.url;
        }

        function onError(error: any): void {
            const errorCode = error.response.error;
            handleError(errorCode);
        }

        if (environment.getFeature("checkout_v2_web")) {
            return callV2Endpoint(buyOption, purchaseIntentSessionId);
        }

        // Propagate the language-country url to checkout to keep in the same domain.
        const endpoint = buildLanguageAwareUrlPath(NATIVE_CHECKOUT_ENDPOINT);
        return callEndpoint(endpoint, buyOption, purchaseIntentSessionId, onSuccess, onError);
    }

    function validateCheckout(event: React.MouseEvent<HTMLElement, MouseEvent>): void {
        if (buttonProps?.onClick) {
            buttonProps.onClick(event);
        }
        if (activeProduct.selectedSizeOption === null && sizePicker.showSizePicker === true) {
            if (isComparison && isDesktopViewport) {
                sizePicker.setComparisonError(true);
                return;
            } else {
                sizePicker.setError(true);
                closeCompareModal();
                return;
            }
        }

        if (!sizePicker.error && !sizePicker.comparisonError) {
            initiateCheckout();
        }
    }

    async function initiateCheckout(): Promise<void> {
        setButtonDisabled(true);
        // Generate a checkout purchase intent session uuid
        // This is used to track checkout analytics
        const sessionId = uuid4.uuid4();

        sendCtaAnalytic({ cta: "buy now" });

        if (buyOption.is_icon) {
            sendCheckoutAnalytics(sessionId, PurchaseIntentCheckoutType.NATIVE);
            await onNativeCheckoutClick(buyOption, sessionId);
        }

        setButtonDisabled(false);
    }

    const buttonTitle = gettext("product.buy_area.buy_now");

    const propsForButton = {
        title: buttonTitle,
        width: "full" as const,
        inactive: buttonDisabled,
        ...buttonProps,
        onClick: validateCheckout,
        dataTestId: "checkout-buy-button",
    };

    return (
        <>
            <ErrorModal
                title={errorModalProperties.title}
                description={errorModalProperties.desc}
                isOpen={errorModalProperties.open}
                onClose={() => setErrorModalProperties({ ...errorModalProperties, open: false })}
                buttonCTAText={errorModalProperties.ctaText}
                buttonCTA={() => setErrorModalProperties({ ...errorModalProperties, open: false })}
            />
            <Button {...propsForButton} />
        </>
    );
}
